import React, { useEffect, useMemo, useState } from "react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ReferenceLine,
    Brush,
} from "recharts";
import { Box, Card, CardContent, Checkbox, FormControlLabel, FormGroup, Stack, Typography } from "@mui/material";
import { ChartSkeleton } from "components/skeletons/ChartSkeleton";
import { EmptyState } from "components/EmptyState";
import { ErrorState } from "components/ErrorState";
import { useAtom, useAtomValue, type PrimitiveAtom } from "jotai";

import { Dropdown } from "components/Dropdown";
import {
    unitIdAtom,
    phenomenonGroupIdAtom,
    visibleSupplementaryXAxesAtom,
    type SupplementaryXAxisKind,
} from "atoms/Ltool/matrixAtoms";
import { allChartDataAtom, allChartLoadingAtom } from "atoms/Ltool/chartAtoms";
import { stageConfigAtom } from "atoms/Ltool/stageConfigAtoms";
import { DRIVE_MODE_IDS, UNIT_IDS, PHENOMENON_GROUP_IDS, PHENOMENON_IDS } from "utils/constsLtool";
import {
    DRIVE_MODE_IDS as HEV_DRIVE_MODE_IDS,
    PHENOMENON_IDS as HEV_PHENOMENON_IDS,
    PHENOMENON_IDS_BY_GROUP as HEV_PHENOMENON_IDS_BY_GROUP,
    CONDITION_GROUP_IDS as HEV_CONDITION_GROUP_IDS,
} from "utils/constsHev";
import { powertrainAtom } from "atoms/powertrainAtom";
import { gearIdAtom } from "atoms/gearAtom";
import { makeTicksFromData, buildIndexMap } from "utils/charts/AxisLabel";
import { getSeriesValueAtAxis } from "utils/charts/getSeriesValueAtAxis";
import { ChartZoomResetButton } from "components/charts/ChartZoomResetButton";
import { YAxisRangeSlider } from "components/charts/YAxisRangeSlider";
import { CROSSHAIR_CURSOR } from "utils/charts/chartConstants";
import { computeSharedYBounds } from "utils/charts/sharedYDomain";
import { resolveCellYAxisRange, useSharedYAxisRange } from "utils/charts/useSharedYAxisRange";
import { useAllChartDataSetter } from "../utils/useAllChartDataSetter";
import { buildConditionIds } from "../utils/utils";
import { buildChartRows, chartYSeries, hasSeriesValue } from "../utils/chartCorrection";
import { allNullData, allStatusError } from "utils/utilsLtool";
import { ACHIEVEMENT_GROUP_IDS } from "utils/achievementConfigForm";
import { DownloadChartCsvButton } from "../download/DownloadChartCsvButton";
import { ChartThreeSigma } from "./ChartThreeSigma";
import { fetchDimensions, fetchDimensionsHev } from "api/fetchLtool";
import { authTokenAtom } from "atoms/authAtoms";

const unitIdDropdownAtom = unitIdAtom as unknown as PrimitiveAtom<string | null>;
const phenomenonGroupDropdownAtom = phenomenonGroupIdAtom as unknown as PrimitiveAtom<string | null>;

const TARGET_COLOR = "#ff2c95";
const MEASUREMENT_COLOR = "#006aff";
// 補正後設計値は設計値の延長（設計値 + 補正値）なので同色の点線にする。
const CORRECTED_DASHARRAY = "5 4";

type LegendItem = { label: string; color: string; value: string; dashed?: boolean };

const formatLegendValue = (value: number | null): string => {
    if (value === null || Number.isNaN(value)) return "-";
    return value.toFixed(2);
};

type MotorOrderStatus = "idle" | "success" | "error";

export const AllConditionChart: React.FC = () => {
    const authToken = useAtomValue(authTokenAtom);
    const unitId = useAtomValue(unitIdAtom);
    const phenomenonGroupId = useAtomValue(phenomenonGroupIdAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);
    const isHev = powertrain === 'hev';
    const driveModeIds: readonly string[] = isHev ? HEV_DRIVE_MODE_IDS : DRIVE_MODE_IDS;
    const [motorOrders, setMotorOrders] = useState<Record<string, number>>({});
    const [motorOrderStatus, setMotorOrderStatus] = useState<MotorOrderStatus>("idle");
    // B1: Brush ズームのリセット用。値を変えると各 LineChart を再マウントして全域へ戻す（表示のみ）。
    const [zoomKey, setZoomKey] = useState(0);
    const [visibleSupplementaryAxes, setVisibleSupplementaryAxes] = useAtom(visibleSupplementaryXAxesAtom);
    const showRpmAxis = visibleSupplementaryAxes.includes("rpm");
    const showVelAxis = visibleSupplementaryAxes.includes("vel");
    const toggleSupplementaryAxis = (kind: SupplementaryXAxisKind) => {
        setVisibleSupplementaryAxes(prev =>
            prev.includes(kind) ? prev.filter(k => k !== kind) : [...prev, kind]
        );
    };

    const conditionIds = buildConditionIds(phenomenonGroupId, isHev);
    useAllChartDataSetter(unitId, conditionIds);

    const allChartData = useAtomValue(allChartDataAtom);
    const isLoading = useAtomValue(allChartLoadingAtom);
    const config = useAtomValue(stageConfigAtom);

    // 描画中セルを横断して縦軸を統一する。集計は実際に <Line> で描画している系列のみ
    // （vel / rpm は補助 X 軸の目盛り用、NG_freq は X 参照線のため対象外）。
    const sharedYBounds = useMemo(
        () =>
            computeSharedYBounds(
                Object.values(allChartData)
                    .filter((entry) => entry?.status === "success" && entry.data)
                    .flatMap((entry) => chartYSeries(entry.data))
            ),
        [allChartData]
    );
    const sharedY = useSharedYAxisRange(sharedYBounds);

    useEffect(() => {
        if (!authToken || !unitId || !config.project_id || (isHev && !gearId)) {
            setMotorOrders({});
            setMotorOrderStatus("idle");
            return;
        }

        let cancelled = false;

        const loadMotorOrders = async () => {
            try {
                // グラフAPIは motor_order を返さないため、諸元APIを別途参照する。
                // 未登録時も 200 + 空 dict で返るため（諸元データ仕様変更 #27）、
                // 「未登録」は motorOrders に該当 phenomenonId が無いことで表現される。
                const dimensions = isHev
                    ? await fetchDimensionsHev(authToken, config, unitId, gearId!)
                    : await fetchDimensions(authToken, config, unitId);
                if (!cancelled) {
                    setMotorOrders(dimensions.motor_order ?? {});
                    setMotorOrderStatus("success");
                }
            } catch (error) {
                if (!cancelled) {
                    console.error("motor_order の取得に失敗しました:", error);
                    setMotorOrders({});
                    setMotorOrderStatus("error");
                }
            }
        };

        loadMotorOrders();

        return () => {
            cancelled = true;
        };
    }, [authToken, config, unitId, isHev, gearId]);

    const phenomenonIdsToDisplay = isHev
        ? (phenomenonGroupId && phenomenonGroupId in HEV_PHENOMENON_IDS_BY_GROUP
            ? [...HEV_PHENOMENON_IDS_BY_GROUP[phenomenonGroupId as keyof typeof HEV_PHENOMENON_IDS_BY_GROUP]]
            : [...HEV_PHENOMENON_IDS])
        : phenomenonGroupId
            ? PHENOMENON_IDS.filter(phen => phen.includes(phenomenonGroupId))
            : [...PHENOMENON_IDS];
    const indexScale = phenomenonGroupId === "aco" ? 10 : 1;

    // 状態判定は従来どおり（優先順: 計算セット未選択 → 該当データなし → 全データ空）。
    const noCalcSet = Object.keys(allChartData).length === 0;
    const hasError = allStatusError(allChartData);
    const allNull = allNullData(allChartData);

    return (
        <Stack spacing={3}>
            <Box display="flex" gap={2} padding={2} alignItems="center">
                <Dropdown stageAtom={unitIdDropdownAtom} options={UNIT_IDS} label="ユニットID" />
                <Dropdown
                    stageAtom={phenomenonGroupDropdownAtom}
                    options={isHev ? HEV_CONDITION_GROUP_IDS : PHENOMENON_GROUP_IDS}
                    label="現象グループID"
                />
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                        表示するX軸:
                    </Typography>
                    <FormGroup row>
                        <FormControlLabel
                            control={<Checkbox size="small" checked disabled />}
                            label="周波数"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={showRpmAxis}
                                    onChange={() => toggleSupplementaryAxis("rpm")}
                                />
                            }
                            label="回転数(RPM)"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={showVelAxis}
                                    onChange={() => toggleSupplementaryAxis("vel")}
                                />
                            }
                            label="車速"
                        />
                    </FormGroup>
                </Box>
            </Box>

            {isHev && !gearId ? (
                <EmptyState message="HEV では Gear を選択してください。" imageAlt="Gear 未選択" />
            ) : isLoading ? (
                <ChartSkeleton height={300} />
            ) : noCalcSet ? (
                <EmptyState
                    message="計算セットが選択されていません。"
                    actions={[{ label: "計算セットを選択", to: "/l-tools/existing-project" }]}
                />
            ) : hasError ? (
                <ErrorState
                    message="指定された条件に合致するデータがありません。"
                    actions={[{ label: "データ登録へ", to: "/l-tools/data-registration" }]}
                />
            ) : allNull ? (
                <ErrorState
                    message="全てのデータが空です。"
                    actions={[{ label: "データ登録へ", to: "/l-tools/data-registration" }]}
                />
            ) : phenomenonIdsToDisplay.length === 0 ? (
                <Typography align="center" variant="body1">
                    選択された現象グループに対応する現象IDがありません。
                </Typography>
            ) : (
                <>
                {/* 縦軸コントロールはグラフ領域の左側、補正値とズームリセットは右端に置く */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, px: 1 }}>
                    <YAxisRangeSlider
                        bounds={sharedY.bounds}
                        range={sharedY.range}
                        onChange={sharedY.setRange}
                        step={sharedY.step}
                        unified={sharedY.unified}
                        onUnifiedChange={sharedY.setUnified}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 2 }}>
                        <ChartThreeSigma
                            allChartData={allChartData}
                            groupIds={ACHIEVEMENT_GROUP_IDS[isHev ? "hev" : "bev"]}
                        />
                        {/* x（Brush 再マウント）と y（選択範囲）の両方を全域へ戻す */}
                        <ChartZoomResetButton
                            onClick={() => {
                                setZoomKey((k) => k + 1);
                                sharedY.reset();
                            }}
                        />
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: `150px repeat(${phenomenonIdsToDisplay.length}, minmax(320px, 1fr))`,
                        gap: 1,
                        overflowX: "auto",
                        p: 1,
                    }}
                >
                    <Box />
                    {phenomenonIdsToDisplay.map(phenomenonId => (
                        <Box
                            key={phenomenonId}
                            sx={{ fontWeight: "bold", textAlign: "center", alignSelf: "center" }}
                        >
                            {phenomenonId}
                        </Box>
                    ))}

                    {driveModeIds.map(driveModeId => (
                        <React.Fragment key={driveModeId}>
                            <Box
                                sx={{
                                    fontWeight: "bold",
                                    display: "flex",
                                    alignItems: "center",
                                    pl: 1,
                                }}
                            >
                                {driveModeId}
                            </Box>

                            {phenomenonIdsToDisplay.map(phenomenonId => {
                                const conditionId = `${phenomenonId}-${driveModeId}`;
                                const entry = allChartData[conditionId];
                                // motor_order は phenomenon_id ごとの flat Dict（BEV/HEV 共通、運転モード非依存）
                                const motorOrder = motorOrders[phenomenonId];
                                const motorOrderLabel =
                                    motorOrderStatus === "error"
                                        ? "取得失敗"
                                        : motorOrderStatus === "success" && motorOrder !== undefined
                                            ? `次数: ${motorOrder}`
                                            : "未登録";

                                let content: React.ReactNode;
                                if (!entry) {
                                    content = (
                                        <Typography variant="body2" align="center">
                                            データなし
                                        </Typography>
                                    );
                                } else if (entry.status === "loading" || entry.status === "notstarted") {
                                    content = (
                                        <Typography variant="body2" align="center">
                                            読み込み中...
                                        </Typography>
                                    );
                                } else if (entry.status === "error") {
                                    content = (
                                        <Typography variant="body2" align="center" color="error">
                                            データ読み込み中にエラーが発生しました。
                                        </Typography>
                                    );
                                } else {
                                    const target = entry.data?.target ?? [];
                                    const measurement = entry.data?.measurement ?? [];
                                    const measurementCorrected = entry.data?.measurement_corrected ?? [];
                                    const chartData = buildChartRows(entry.data, indexScale);

                                    if (chartData.length === 0) {
                                        content = (
                                            <Typography variant="body2" align="center">
                                                データが空です。
                                            </Typography>
                                        );
                                    } else {
                                        // 未登録・未計算（[]）や BE 未反映では点線も凡例も出さない。
                                        const showCorrected = hasSeriesValue(measurementCorrected);
                                        const ticks = makeTicksFromData(chartData, "index", 250 * indexScale);
                                        const cellY = resolveCellYAxisRange(chartYSeries(entry.data), sharedY);
                                        const rpmByIndex = buildIndexMap(chartData, "index", "rpm");
                                        const velByIndex = buildIndexMap(chartData, "index", "vel");

                                        const referenceLineValue =
                                            typeof entry.data?.NG_freq === "number" ? entry.data.NG_freq : null;

                                        const referenceTargetValue =
                                            referenceLineValue !== null
                                                ? getSeriesValueAtAxis(target, referenceLineValue, indexScale)
                                                : null;
                                        const referenceMeasurementValue =
                                            referenceLineValue !== null
                                                ? getSeriesValueAtAxis(measurement, referenceLineValue, indexScale)
                                                : null;
                                        const referenceCorrectedValue =
                                            referenceLineValue !== null && showCorrected
                                                ? getSeriesValueAtAxis(measurementCorrected, referenceLineValue, indexScale)
                                                : null;
                                        const legend =
                                            referenceLineValue !== null ? (
                                                <Stack
                                                    direction="row"
                                                    spacing={1.5}
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    flexWrap="wrap"
                                                    sx={{ fontSize: 10, color: "text.secondary" }}
                                                >
                                                    {([
                                                        { label: "周波数", color: "#d32f2f", value: referenceLineValue.toFixed(0) },
                                                        {
                                                            label: "目標値",
                                                            color: TARGET_COLOR,
                                                            value: formatLegendValue(referenceTargetValue),
                                                        },
                                                        {
                                                            label: "設計値",
                                                            color: MEASUREMENT_COLOR,
                                                            value: formatLegendValue(referenceMeasurementValue),
                                                        },
                                                        ...(showCorrected
                                                            ? [{
                                                                label: "補正後設計値",
                                                                color: MEASUREMENT_COLOR,
                                                                value: formatLegendValue(referenceCorrectedValue),
                                                                dashed: true,
                                                            }]
                                                            : []),
                                                    ] as LegendItem[]).filter(Boolean).map(item => (
                                                        item && (
                                                            <Box
                                                                key={item.label}
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 0.5,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={item.dashed
                                                                        ? { width: 12, height: 0, borderTop: "2px dashed", borderColor: item.color }
                                                                        : { width: 12, height: 2, bgcolor: item.color }}
                                                                />
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{ color: "text.secondary", fontWeight: 600 }}
                                                                >
                                                                    {`${item.label}: ${item.value}`}
                                                                </Typography>
                                                            </Box>
                                                        )
                                                    ))}
                                                </Stack>
                                            ) : null;

                                        content = (
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    height: "100%",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: legend ? 0.5 : 0,
                                                }}
                                            >
                                                {legend}
                                                <Box sx={{ flex: 1, minHeight: 0 }}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <LineChart
                                                            key={`${conditionId}-${zoomKey}`}
                                                            data={chartData}
                                                            margin={{ top: 12, right: 20, left: -20 }}
                                                        >
                                                            <XAxis
                                                                dataKey="index"
                                                                xAxisId="0"
                                                                height={18}
                                                                ticks={ticks}
                                                                tick={{ fontSize: 8 }}
                                                                label={{ value: "Freq.", position: "insideLeft", offset: -20, fontSize: 8 }}
                                                            />
                                                            {showRpmAxis && (
                                                                <XAxis
                                                                    dataKey="index"
                                                                    xAxisId="1"
                                                                    height={18}
                                                                    ticks={ticks}
                                                                    tick={{ fontSize: 8 }}
                                                                    tickFormatter={(v: number) =>
                                                                        rpmByIndex.has(v) ? Math.round(rpmByIndex.get(v)!).toString() : ""
                                                                    }
                                                                    label={{ value: "RPM", position: "insideLeft", offset: -20, fontSize: 8 }}
                                                                />
                                                            )}
                                                            {showVelAxis && (
                                                                <XAxis
                                                                    dataKey="index"
                                                                    xAxisId="2"
                                                                    height={18}
                                                                    ticks={ticks}
                                                                    tick={{ fontSize: 8 }}
                                                                    tickFormatter={(v: number) =>
                                                                        velByIndex.has(v) ? Math.round(velByIndex.get(v)!).toString() : ""
                                                                    }
                                                                    label={{ value: "Vel.", position: "insideLeft", offset: -20, fontSize: 8 }}
                                                                />
                                                            )}
                                                            <YAxis
                                                                tick={{ fontSize: 8 }}
                                                                domain={cellY.domain}
                                                                ticks={cellY.ticks}
                                                                allowDataOverflow={cellY.domain !== undefined}
                                                            />
                                                            <Tooltip
                                                                cursor={CROSSHAIR_CURSOR}
                                                                formatter={(value: unknown, name: string) => {
                                                                    if (typeof value === "number") {
                                                                        return [value.toFixed(3), name];
                                                                    }
                                                                    return ["-", name];
                                                                }}
                                                                labelFormatter={(label) => `周波数: ${label}`}
                                                                contentStyle={{ fontSize: 10, padding: "4px 8px" }}
                                                                labelStyle={{ fontSize: 10, marginBottom: 2 }}
                                                                itemStyle={{ fontSize: 10, padding: 0 }}
                                                                offset={30}
                                                                wrapperStyle={{ pointerEvents: "none" }}
                                                            />
                                                            <Line
                                                                type="monotone"
                                                                dataKey="target"
                                                                name="target"
                                                                stroke={TARGET_COLOR}
                                                                dot={false}
                                                                isAnimationActive={false}
                                                            />
                                                            <Line
                                                                type="monotone"
                                                                dataKey="measurement"
                                                                name="measurement"
                                                                stroke={MEASUREMENT_COLOR}
                                                                dot={false}
                                                                isAnimationActive={false}
                                                            />
                                                            {showCorrected && (
                                                                <Line
                                                                    type="monotone"
                                                                    dataKey="measurement_corrected"
                                                                    name="補正後設計値"
                                                                    stroke={MEASUREMENT_COLOR}
                                                                    strokeDasharray={CORRECTED_DASHARRAY}
                                                                    dot={false}
                                                                    isAnimationActive={false}
                                                                />
                                                            )}
                                                            {referenceLineValue !== null && (
                                                                <ReferenceLine
                                                                    x={referenceLineValue}
                                                                    stroke="#d32f2f"
                                                                    strokeDasharray="6 4"
                                                                />
                                                            )}
                                                            <Brush
                                                                dataKey="index"
                                                                height={15}
                                                                travellerWidth={5}
                                                                startIndex={0}
                                                                endIndex={chartData.length - 1}
                                                            />
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "flex-end",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        pt: 0.5,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            minHeight: 24,
                                                            px: 1,
                                                            border: 1,
                                                            borderColor: "divider",
                                                            borderRadius: 1,
                                                            color: "text.secondary",
                                                            fontSize: "0.5rem",
                                                            lineHeight: 1.2,
                                                            bgcolor: "background.paper",
                                                        }}
                                                    >
                                                        {motorOrderLabel}
                                                    </Box>
                                                    <DownloadChartCsvButton
                                                        chartData={chartData}
                                                        unitId={unitId}
                                                        phenomenonGroupId={phenomenonGroupId}
                                                        conditionId={conditionId}
                                                    />
                                                </Box>
                                            </Box>
                                        );
                                    }
                                }

                                return (
                                    <Card
                                        key={conditionId}
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            minWidth: 320,
                                            flexShrink: 0,
                                            height: 260,
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                p: 1,
                                                flex: 1,
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                            }}
                                        >
                                            {content}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </Box>
                </>
            )}
        </Stack>
    );
};
